/**
 * Still versus moving (MVP notes §17).
 *
 * Both are states rather than actions, so both are latched: a child standing quietly produces one
 * PLAYER_BECAME_STILL, and setting off again produces one PLAYER_MOVED.
 *
 * Speed is measured on the torso centre in body scales per second, so a child closer to the camera
 * is not a child who moves faster. Becoming still is the stricter of the two thresholds (the
 * calibrated hysteresis) and must hold for `pose.stillnessHoldMs`, because stillness read from a
 * single frame is a reading of the shutter rather than of the child.
 *
 * An empty room is the stillest thing a camera ever sees, and it is emphatically not a child
 * holding still — which is why the gate's `lost` clears the hold rather than completing it.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../../platform/config/tunables';
import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { DEFAULT_POSE_CALIBRATION } from '../pose-calibration';
import { torsoCentre, type Point } from '../pose-geometry';
import { POSE_LANDMARK } from '../pose-types';
import { createTrackingGate } from './tracking-gate';

const STILLNESS_LANDMARKS = [
  POSE_LANDMARK.leftShoulder,
  POSE_LANDMARK.rightShoulder,
  POSE_LANDMARK.leftHip,
  POSE_LANDMARK.rightHip,
];

type Motion = 'unknown' | 'still' | 'moving';

export function createStillnessDetector(options: DetectorOptions = {}): PoseDetector {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const calibration = options.calibration ?? DEFAULT_POSE_CALIBRATION;
  const gate = createTrackingGate({ landmarks: STILLNESS_LANDMARKS, tunables });

  const movingSpeed = calibration.bodyScale * calibration.stillnessSpeedScale;
  const stillSpeed = movingSpeed * calibration.hysteresisFraction;

  let previous: { point: Point; at: number } | null = null;
  let state: Motion = 'unknown';
  let stillSince: number | null = null;
  /**
   * Neutral re-acquisition (D-068(2)): the first speed reading after a reset or a loss *adopts*
   * whichever state the body is already in, silently.
   *
   * Without it a body that never moved announces `PLAYER_BECAME_STILL` again after every reset, and
   * an interval with no frames in it looks like the stillest a room has ever been.
   */
  let awaitingBaseline = false;

  const forget = (): void => {
    previous = null;
    state = 'unknown';
    stillSince = null;
    awaitingBaseline = true;
  };

  return {
    capability: 'stillness',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);
      if (reading.kind === 'blocked') {
        if (reading.lost) forget();
        return reading.outcome;
      }

      const centre = torsoCentre(reading.pose);
      if (centre === null) return { kind: 'none' };

      const last = previous;
      previous = { point: centre, at: reading.at };

      // The first frame after a start or a loss has nothing to compare against.
      if (last === null || reading.at <= last.at) return { kind: 'none' };

      const seconds = (reading.at - last.at) / 1_000;
      const distance = Math.hypot(centre.x - last.point.x, centre.y - last.point.y);
      const speed = distance / seconds;

      if (awaitingBaseline) {
        awaitingBaseline = false;
        state = speed > movingSpeed ? 'moving' : 'still';
        if (state === 'still') stillSince = last.at;
        return { kind: 'none' };
      }

      if (speed > movingSpeed) {
        stillSince = null;
        if (state === 'moving') return { kind: 'none' };
        state = 'moving';
        return { kind: 'event', event: { type: 'PLAYER_MOVED' }, confidence: reading.confidence };
      }

      if (speed > stillSpeed) return { kind: 'none' };

      if (stillSince === null) stillSince = last.at;
      if (state === 'still') return { kind: 'none' };
      if (reading.at - stillSince < tunables.pose.stillnessHoldMs) return { kind: 'none' };

      state = 'still';
      return {
        kind: 'event',
        event: { type: 'PLAYER_BECAME_STILL' },
        confidence: reading.confidence,
      };
    },

    /** The hold part-way to being still is discarded; what the body is doing is not (S2-3). */
    discardInProgress(): void {
      stillSince = null;
    },

    /**
     * A hole in the observation: stillness is a claim about a stretch of *watched* time (S5-5).
     *
     * A body that was still before a hole and still after it may have moved throughout the middle,
     * and this detector cannot tell. So the stretch starts again from the returning frame.
     */
    reacquireSilently(): void {
      forget();
    },

    reset(): void {
      gate.reset();
      forget();
    },
  };
}
